
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

const AuthRedirect: React.FC = () => {
  const { user, loading, refreshSession } = useAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/index';
  const [error, setError] = useState<string | null>(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Effet pour gérer les cas où le chargement prend trop de temps
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (loading) {
      // Après 5 secondes de chargement, vérifier manuellement la session
      timeoutId = setTimeout(async () => {
        setLoadingTimeout(true);
        try {
          // Vérifier directement la session
          const { data } = await supabase.auth.getSession();
          if (data.session) {
            console.log("Session trouvée, mais loading ne s'est pas terminé");
            // On a une session mais le loading n'a pas terminé, forcer le rafraîchissement
            await refreshSession();
          } else {
            setError("Impossible de vérifier votre session. Vous pouvez essayer de vous connecter à nouveau.");
          }
        } catch (err) {
          console.error("Erreur lors de la vérification de session:", err);
          setError("Erreur de communication avec le serveur d'authentification");
        }
      }, 5000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading, refreshSession]);

  // Gérer une tentative de reconnexion
  const handleRetry = async () => {
    setError(null);
    await refreshSession();
  };

  // Redirection vers la page de login si l'utilisateur reste bloqué
  const handleGoToLogin = () => {
    localStorage.removeItem('supabase.auth.token');
    localStorage.removeItem('sb-woydvhczdsaehgccyqvp-auth-token');
  };

  // Si connecté, rediriger directement vers la page cible
  if (user && !loading) {
    console.log("Utilisateur authentifié, redirection vers", from);
    return <Navigate to={from} replace />;
  }

  // Si chargement, afficher indicateur
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-muted-foreground">Vérification de votre session...</p>
        
        {/* Afficher un message d'erreur et options de récupération après timeout */}
        {(loadingTimeout || error) && (
          <div className="mt-8 max-w-md text-center">
            <div className="flex items-center text-red-500 mb-4">
              <AlertCircle className="h-5 w-5 mr-2" />
              <p>{error || "La vérification prend plus de temps que prévu."}</p>
            </div>
            <div className="flex gap-3 justify-center mt-4">
              <Button 
                onClick={handleRetry} 
                variant="outline"
              >
                Réessayer
              </Button>
              <Button 
                onClick={handleGoToLogin}
                variant="default"
                asChild
              >
                <a href="/login">Se connecter</a>
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Si pas connecté, rediriger vers login
  console.log("Utilisateur non authentifié, redirection vers login");
  return <Navigate to="/login" replace />;
};

export default AuthRedirect;
